import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { createStore } from '@/lib/store';
import { api, post } from '@/lib/api';
import { fmtDay } from '@/lib/format';
import { attempt } from '@/lib/notify';
import type { OwnEvent } from '@/lib/types';

const store = createStore<{ feedId: string; onDone: () => void } | null>(null);
export const openDeletedDialog = (feedId: string, onDone: () => void) => store.set({ feedId, onDone });

export function DeletedDialog() {
  const s = store.use();
  const q = useQuery({
    queryKey: ['deleted', s?.feedId],
    queryFn: async () => (await api<{ events: OwnEvent[] }>(`/feeds/${s!.feedId}/events?deleted=1`)).events.filter((e) => e.deleted_at).sort((a, b) => (b.deleted_at ?? 0) - (a.deleted_at ?? 0)),
    enabled: !!s,
    staleTime: 0,
  });
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && store.set(null)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recently deleted</DialogTitle>
          <DialogDescription>Deleted events are gone from this calendar and from subscribers’ calendars. Restore one to bring it back.</DialogDescription>
        </DialogHeader>
        <div className="max-h-96 divide-y overflow-y-auto">
          {q.data?.length ? (
            q.data.map((e) => (
              <div key={e.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{e.title}</p>
                  <p className="text-muted-foreground text-xs">{e.all_day ? fmtDay(e.start) : `${fmtDay(e.start)} ${e.start.slice(11, 16)}`}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (await attempt(() => post(`/feeds/${s!.feedId}/events/${e.id}/restore`), 'Event restored')) {
                      store.set(null);
                      s!.onDone();
                    }
                  }}
                >
                  Restore
                </Button>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground py-4 text-sm">{q.isPending ? 'Loading…' : 'Nothing deleted recently.'}</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
